"use client";

import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-[var(--border)] bg-[var(--card)] backdrop-blur-xl">
        <p className="font-mono text-xs uppercase tracking-widest text-[var(--primary)] mb-4">— TRANSMISSION RECEIVED</p>
        <p className="font-default text-xl text-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-[var(--border)]">
          SEND ANOTHER REQUEST
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">— NAME</FormLabel>
            <FormControl>
              <Input {...field} placeholder="Your full name" className="border-0 border-b border-[var(--border)] bg-transparent px-0 h-12 font-default text-lg focus-visible:ring-0 focus-visible:border-[var(--primary)] transition-colors" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">— EMAIL</FormLabel>
            <FormControl>
              <Input {...field} type="email" placeholder="you@domain.com" className="border-0 border-b border-[var(--border)] bg-transparent px-0 h-12 font-default text-lg focus-visible:ring-0 focus-visible:border-[var(--primary)] transition-colors" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      pattern: {
        value: /^[+\d\s()-]{7,}$/,
        message: "Invalid phone"
      }
    }} render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">— TELEPHONE</FormLabel>
            <FormControl>
              <Input {...field} type="tel" placeholder="(561) 560-6653" className="border-0 border-b border-[var(--border)] bg-transparent px-0 h-12 font-default text-lg focus-visible:ring-0 focus-visible:border-[var(--primary)] transition-colors" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">— SERVICE TYPE</FormLabel>
            <FormControl>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="border-0 border-b border-[var(--border)] bg-transparent px-0 h-12 font-default text-lg focus-visible:ring-0 focus-visible:border-[var(--primary)] w-full">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="border-[var(--border)] bg-[var(--popover)] font-mono text-xs uppercase tracking-widest">
                  <SelectItem value="inspection" className="">MOLD INSPECTION</SelectItem>
                  <SelectItem value="testing" className="">ENVIRONMENTAL TESTING</SelectItem>
                  <SelectItem value="consultation" className="">CONSULTATION</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Min 10 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">— PROPERTY DETAILS</FormLabel>
            <FormControl>
              <Textarea {...field} placeholder="Describe your concerns or property location..." rows={5} className="border-0 border-b border-[var(--border)] bg-transparent px-0 font-default text-lg focus-visible:ring-0 focus-visible:border-[var(--primary)] transition-colors resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-[var(--destructive)] bg-[var(--destructive)]/10">
          <p className="text-[var(--destructive)] font-mono text-xs uppercase tracking-widest">{submitError}</p>
        </div>}

      <div className="pt-4 space-y-6">
        <div className="flex items-center gap-3">
          <span className="relative flex size-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-lg bg-[var(--primary)] opacity-75"></span>
            <span className="relative inline-flex size-2 rounded-lg bg-[var(--primary)]"></span>
          </span>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            CERTIFIED EXPERTISE — 26 YEARS EXPERIENCE
          </p>
        </div>

        <Button type="submit" disabled={isSubmitting} className="w-full rounded-lg h-14 bg-[var(--primary)] text-[var(--primary-foreground)] hover:bg-[var(--primary)]/90 font-default text-sm uppercase tracking-[0.2em] shadow-lg">
          {isSubmitting ? "SENDING..." : "REQUEST INSPECTION →"}
        </Button>
      </div>
    </Form>;
}
export default function Inspection() {
  const contactMethods = [{
    label: "EMAIL",
    value: "Drmold@me.com",
    to: "mailto:Drmold@me.com"
  }, {
    label: "TELEPHONE",
    value: "(561) 560-6653",
    to: "tel:+15615606653"
  }, {
    label: "SERVICE AREA",
    value: "DADE • BROWARD • WEST PALM",
    to: "/ServicesPage"
  }];
  return <section data-section-id="4321" id="request-an-inspection" className="bg-background text-foreground relative overflow-hidden py-20 lg:py-28 px-6 lg:px-12 min-h-screen flex items-center">
      <div className="absolute top-1/4 -left-40 size-96 rounded-lg bg-[var(--primary)] opacity-10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 -right-40 size-96 rounded-lg bg-[var(--primary)] opacity-5 blur-[120px] pointer-events-none" />

      <div className="relative w-full max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[45fr_55fr] gap-12 lg:gap-16 items-start">
        <div className="space-y-10 lg:sticky lg:top-24">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--primary)]">
            — CLINICAL TRUST
          </p>

          <h1 className="font-default font-black uppercase leading-[0.85] tracking-tighter text-foreground text-[clamp(3.5rem,11vw,10rem)]">
            SECURE
            <br />
            YOUR
            <br />
            SPACE.
          </h1>

          <p className="font-default text-lg lg:text-[19px] leading-relaxed text-muted-foreground max-w-[480px]">
            MYB Services provides certified, evidence-based environmental testing. Protect your property with 26 years of professional expertise.
          </p>

          <div className="pt-6 border-t border-[var(--border)] space-y-1">
            {contactMethods.map((method, i) => <Link key={i} data-index={i} to={method.to} className="group flex items-center justify-between gap-4 py-5 border-b border-[var(--border)] hover:bg-[var(--accent)] transition-colors px-2 -mx-2">
                <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 min-w-0">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground shrink-0">
                    — {method.label}
                  </span>
                  <span className="font-default text-base sm:text-lg text-foreground truncate">
                    {method.value}
                  </span>
                </div>
                <ArrowUpRight className="size-5 text-[var(--primary)] shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
              </Link>)}
          </div>
        </div>

        <FormProvider formKey="request_inspection_form_contact" formId="request_inspection_form_contact" sectionName="request-an-inspection" defaultValues={{
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          pattern: "phone"
        },
        message: {
          required: true,
          minLength: 10
        }
      }} submitText="Request Inspection" successMessage="Your request has been received. A certified technician will contact you shortly." errorMessage="Something went wrong. Please try again or call us directly." className="relative">
          <div className="relative border border-[var(--border)] bg-[var(--card)] backdrop-blur-2xl p-8 sm:p-12 lg:p-14 shadow-lg">
            <div className="absolute inset-0 bg-gradient-to-br from-white/[0.04] to-transparent pointer-events-none" />

            <div className="relative">
              <div className="mb-10 pb-6 border-b border-[var(--border)]">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--primary)] mb-3">
                  — CLINICAL INTAKE
                </p>
                <h2 className="font-default text-2xl sm:text-3xl font-bold uppercase tracking-tight text-foreground">
                  Schedule Inspection
                </h2>
              </div>

              <ContactForm />
            </div>
          </div>
        </FormProvider>
      </div>
    </section>;
}